import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, FileCheck, FlaskConical, Package, ShieldCheck, Truck, BookOpen, Layers } from 'lucide-react';
import api from '../lib/api';
import { useSite } from '../context/SiteContext';
import ProductCard from '../components/ProductCard';
import { CATEGORIES, catOf, setPageMeta, fmtDate } from '../lib/shop';

export default function Home() {
  const { site } = useSite();
  const [popular, setPopular] = useState([]);
  const [arrivals, setArrivals] = useState([]);
  const [articles, setArticles] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const navigate = useNavigate();
  const hero = site.hero || {};

  useEffect(() => {
    setPageMeta('', 'VQ Health — premium UK research reference compounds with batch-level documentation and COA library.');
    api.get('/products', { params: { popular: '1', limit: 4 } }).then((r) => setPopular(r.data.slice(0, 4))).catch(() => {});
    api.get('/products', { params: { new: '1', limit: 4 } }).then((r) => setArrivals(r.data.slice(0, 4))).catch(() => {});
    api.get('/journal').then((r) => setArticles(r.data.slice(0, 3))).catch(() => {});
    api.get('/faqs').then((r) => setFaqs(r.data.slice(0, 4))).catch(() => {});
  }, []);

  return (
    <div data-testid="homepage">
      {/* HERO */}
      <section className="border-b hairline">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-24 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <p className="font-mono2 text-[11px] uppercase tracking-[0.25em] text-[#8A8F95] fade-up">{hero.eyebrow || 'UK RESEARCH COMPOUND SUPPLY'}</p>
            <h1 className="font-display font-black uppercase tracking-tight leading-[0.95] text-5xl sm:text-6xl lg:text-7xl mt-4 fade-up fade-up-1">
              {hero.title || 'PRECISION COMPOUNDS FOR SERIOUS RESEARCH'}
            </h1>
            <p className="text-base sm:text-lg text-[#575B60] leading-relaxed mt-6 max-w-xl fade-up fade-up-2">
              {hero.subtitle}
            </p>
            <div className="flex flex-wrap gap-3 mt-8 fade-up fade-up-3">
              <Link to="/products" data-testid="hero-explore-cta"
                className="bg-[#0B0F12] text-[#FAFAF8] px-7 py-4 font-mono2 text-xs uppercase tracking-[0.2em] hover:bg-[#E54D2E] transition-colors flex items-center gap-2">
                Explore Products <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/coas" data-testid="hero-coa-cta"
                className="border border-[#0B0F12] px-7 py-4 font-mono2 text-xs uppercase tracking-[0.2em] hover:bg-[#0B0F12] hover:text-[#FAFAF8] transition-colors flex items-center gap-2">
                <FileCheck className="w-4 h-4" /> View COAs
              </Link>
            </div>
            <p className="font-mono2 text-[10px] uppercase tracking-[0.18em] text-[#8A8F95] mt-8 fade-up fade-up-4">
              {site.research_notice}
            </p>
          </div>
          <div className="lg:col-span-5 fade-up fade-up-2">
            <div className="border hairline bg-white p-2">
              <img src={hero.image} alt="VQ Health laboratory vial" className="w-full aspect-[4/3] object-cover" />
            </div>
            <div className="grid grid-cols-3 border hairline border-t-0 bg-white divide-x divide-[#E5E5DF]">
              {[['Batch', 'Tracked'], ['COA', 'Library'], ['UK', 'Dispatch']].map(([a, b]) => (
                <div key={a} className="py-3 text-center">
                  <p className="font-mono2 text-[9px] uppercase tracking-[0.2em] text-[#8A8F95]">{a}</p>
                  <p className="font-display font-bold uppercase text-sm">{b}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CATEGORY TILES */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="flex items-end justify-between mb-8">
          <h2 className="font-display font-black uppercase text-3xl sm:text-4xl tracking-tight">Research Categories</h2>
          <Link to="/products" className="font-mono2 text-[11px] uppercase tracking-widest text-[#575B60] hover:text-[#0B0F12] hidden sm:flex items-center gap-1">All products <ArrowRight className="w-3.5 h-3.5" /></Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {Object.entries(CATEGORIES).map(([key, c]) => (
            <button key={key} data-testid={`category-tile-${key}`} onClick={() => navigate(`/products?category=${key}`)}
              className="group text-left bg-white border hairline hover:border-[#0B0F12] transition-colors p-5 min-h-[130px] flex flex-col justify-between">
              <span className="w-8 h-1" style={{ background: c.color }} />
              <div>
                <p className="font-display font-bold uppercase text-lg leading-tight mt-3 group-hover:underline">{c.name}</p>
                <p className="font-mono2 text-[9px] uppercase tracking-[0.15em] text-[#8A8F95] mt-1">{c.tag}</p>
              </div>
            </button>
          ))}
          <button data-testid="category-tile-all" onClick={() => navigate('/products')}
            className="text-left bg-[#0B0F12] text-[#FAFAF8] p-5 min-h-[130px] flex flex-col justify-between hover:bg-[#1A2129] transition-colors">
            <Layers className="w-5 h-5" />
            <p className="font-display font-bold uppercase text-lg leading-tight mt-3">Full Catalogue</p>
          </button>
        </div>
      </section>

      {/* POPULAR */}
      {popular.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div className="flex items-end justify-between mb-8">
            <h2 className="font-display font-black uppercase text-3xl sm:text-4xl tracking-tight">Popular Research</h2>
            <Link to="/products?sort=featured" className="font-mono2 text-[11px] uppercase tracking-widest text-[#575B60] hover:text-[#0B0F12] flex items-center gap-1">View all <ArrowRight className="w-3.5 h-3.5" /></Link>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
            {popular.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        </section>
      )}

      {/* NEW ARRIVALS */}
      {arrivals.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <h2 className="font-display font-black uppercase text-3xl sm:text-4xl tracking-tight mb-8">New Arrivals</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
            {arrivals.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        </section>
      )}

      {/* FORMATS */}
      <section className="bg-white border-y hairline mt-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <h2 className="font-display font-black uppercase text-3xl sm:text-4xl tracking-tight mb-2">Research Formats</h2>
          <p className="text-[#575B60] mb-10 max-w-2xl">Every format is supplied sealed and batch-labelled for laboratory traceability.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { icon: FlaskConical, name: 'Vials', desc: 'Lyophilised and prepared research vials with crimp-sealed closures.', to: '/products?format=Vial' },
              { icon: Package, name: 'Pens', desc: 'Pre-filled research pen devices for volumetric laboratory work.', to: '/products?format=Pen' },
              { icon: Layers, name: 'Sprays', desc: 'Research spray preparations in metered laboratory dispensers.', to: '/products?format=Spray' },
              { icon: ShieldCheck, name: 'Complexes', desc: 'Multi-compound research blends supplied as matched sets.', to: '/products?category=specialty' },
            ].map((f) => (
              <Link key={f.name} to={f.to} data-testid={`format-${f.name.toLowerCase()}`}
                className="border hairline p-6 hover:border-[#0B0F12] transition-colors group">
                <f.icon className="w-6 h-6 text-[#575B60] group-hover:text-[#0B0F12] transition-colors" />
                <p className="font-display font-bold uppercase text-xl mt-4">{f.name}</p>
                <p className="text-sm text-[#575B60] mt-2 leading-relaxed">{f.desc}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* TESTING / COA EXPLANATION */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
          <div>
            <p className="font-mono2 text-[11px] uppercase tracking-[0.25em] text-[#8A8F95]">Documentation & Testing</p>
            <h2 className="font-display font-black uppercase text-3xl sm:text-4xl tracking-tight mt-3">Batch-Level Transparency</h2>
            <p className="text-[#575B60] leading-relaxed mt-4">
              Every VQ Health product page shows the batch currently being supplied. Where a Certificate of
              Analysis exists for that batch, it is published in full in our COA library — product, batch,
              testing laboratory, test date and result against specification.
            </p>
            <p className="text-[#575B60] leading-relaxed mt-3">
              We never publish generic or representative certificates, and we never invent test results.
              If a batch has no COA, the product page says so plainly.
            </p>
            <div className="flex gap-3 mt-7">
              <Link to="/coas" data-testid="home-coa-library-cta" className="bg-[#0B0F12] text-[#FAFAF8] px-6 py-3.5 font-mono2 text-xs uppercase tracking-[0.2em] hover:bg-[#E54D2E] transition-colors">Search COA Library</Link>
              <Link to="/docs/coas" className="border border-[#0B0F12] px-6 py-3.5 font-mono2 text-xs uppercase tracking-[0.2em] hover:bg-[#F4F4F0] transition-colors">How COAs work</Link>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {[
              { icon: FileCheck, t: 'COA Library', d: 'Searchable by product or batch number with original PDFs.' },
              { icon: ShieldCheck, t: 'Batch Traceability', d: 'The batch on your vial matches the batch on the certificate.' },
              { icon: Truck, t: 'Tracked UK Dispatch', d: 'Protective, discreet packaging with tracked delivery options.' },
              { icon: BookOpen, t: 'Research Education', d: 'Guides on storage, handling and documentation literacy.' },
            ].map((x) => (
              <div key={x.t} className="bg-white border hairline p-5">
                <x.icon className="w-5 h-5 text-[#0B0F12]" />
                <p className="font-display font-bold uppercase text-base mt-3">{x.t}</p>
                <p className="text-[13px] text-[#575B60] mt-1.5 leading-relaxed">{x.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* JOURNAL PREVIEW */}
      {articles.length > 0 && (
        <section className="bg-white border-y hairline">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <div className="flex items-end justify-between mb-8">
              <h2 className="font-display font-black uppercase text-3xl sm:text-4xl tracking-tight">Research Journal</h2>
              <Link to="/journal" className="font-mono2 text-[11px] uppercase tracking-widest text-[#575B60] hover:text-[#0B0F12] flex items-center gap-1">All articles <ArrowRight className="w-3.5 h-3.5" /></Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {articles.map((a) => (
                <Link key={a.id} to={`/journal/${a.slug}`} data-testid={`journal-preview-${a.slug}`}
                  className="border hairline p-6 hover:border-[#0B0F12] transition-colors group flex flex-col">
                  <p className="font-mono2 text-[10px] uppercase tracking-[0.18em] text-[#8A8F95]">{a.category} · {a.reading_time} min read</p>
                  <p className="font-display font-bold uppercase text-xl leading-tight mt-3 group-hover:underline">{a.title}</p>
                  <p className="text-sm text-[#575B60] mt-3 leading-relaxed line-clamp-3">{a.excerpt}</p>
                  <p className="font-mono2 text-[10px] uppercase tracking-widest text-[#8A8F95] mt-auto pt-4">{fmtDate(a.publish_at)}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FAQ PREVIEW */}
      {faqs.length > 0 && (
        <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <h2 className="font-display font-black uppercase text-3xl sm:text-4xl tracking-tight mb-8 text-center">Common Questions</h2>
          <div className="space-y-3">
            {faqs.map((f) => (
              <details key={f.id} className="bg-white border hairline group" data-testid={`home-faq-${f.id}`}>
                <summary className="cursor-pointer px-5 py-4 font-display font-bold uppercase tracking-wide text-base list-none flex justify-between items-center">
                  {f.question}
                  <span className="font-mono2 text-[#8A8F95] group-open:rotate-45 transition-transform text-xl leading-none">+</span>
                </summary>
                <p className="px-5 pb-5 text-sm text-[#575B60] leading-relaxed">{f.answer}</p>
              </details>
            ))}
          </div>
          <div className="text-center mt-8">
            <Link to="/faq" className="font-mono2 text-[11px] uppercase tracking-widest underline underline-offset-4">View full FAQ</Link>
          </div>
        </section>
      )}
    </div>
  );
}
